import { useMutation, useQuery } from "react-query";
import {
  checkHeartBeat,
  getPlatformInfo,
  hasEmployeePortalAccess,
  isRetryableIdentityError,
  refetchAccessToken,
} from "../api/idServer";
import type { EquionSession } from "../api/idServer";
import { redirectAfterSignOut, signOutAdmin } from "../api/adminAuth";

export function useSignOut() {
  return useMutation({
    mutationKey: "signOut",
    mutationFn: signOutAdmin,
    onSuccess: redirectAfterSignOut,
    onError: () => {},
  });
}

export function useCheckHeartBeat(enabled: boolean) {
  return useQuery({
    queryKey: "heartbeat",
    queryFn: checkHeartBeat,
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
    refetchOnWindowFocus: "always", // Refetch when the tab regains focus
    staleTime: 0, // Ensure data is treated as stale immediately
    cacheTime: 0, // Avoid caching if unnecessary
    retry: (failureCount, error) =>
      failureCount < 2 && isRetryableIdentityError(error),
    retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
    enabled,
  });
}

export function useRefetchAccessToken(enabled: boolean) {
  return useQuery({
    queryFn: () => refetchAccessToken(),
    queryKey: "refetchAccessToken",
    enabled,
    // Token issuance rotates the refresh cookie, so repeat it at most once.
    retry: (failureCount, error) =>
      failureCount < 1 && isRetryableIdentityError(error),
    retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useGetPlatformInfo(accesstoken: string, enabled?: boolean) {
  return useQuery({
    queryKey: ["getPlatformInfo", accesstoken],
    queryFn: getPlatformInfo,
    enabled,
  });
}

export function useEmployeePortalAccess(session: EquionSession | null) {
  return useQuery({
    queryKey: ["employeePortalAccess", session?.userId, session?.sid],
    queryFn: () => hasEmployeePortalAccess(session!),
    enabled: Boolean(session?.userId && session?.sid),
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: "always",
  });
}
